import { useEffect } from "react";
import { zodResolver } from "@hookform/resolvers/zod";
import { useQueryClient } from "@tanstack/react-query";
import { useForm } from "react-hook-form";
import { Link, Navigate, useNavigate } from "react-router-dom";
import { jt, t } from "ttag";
import { z } from "zod";

import { ProtectedInformation } from "@/assets/onboarding/protected-information";
import { EXTERNAL_LINK } from "@/config/external-links";
import { useCustomerIoContext } from "@/features/analytics/providers";
import { trackAffiliateIdCaptured } from "@/features/analytics/trackers/affiliate-id-captured";
import { trackEmailPhonePageViewed } from "@/features/analytics/trackers/email-phone-page-viewed";
import { hasEverflowAttribution } from "@/features/analytics/triggers/customer-io";
import { useCreateUser } from "@/features/onboarding/hooks";
import { contactInfoSchema, SIGNUP_FUNNEL } from "@/features/onboarding/models";
import { ONBOARDING_ROUTE } from "@/features/onboarding/router";
import { useOnboardingStore } from "@/features/onboarding/stores";
import { productsQueriesStore } from "@/features/products/api";
import {
  LogoLoader,
  SmsMarketingConsentField,
  TermsAndConditionsField,
} from "@/shared/components";
import { CATEGORY_SLUG } from "@/shared/domains";
import {
  Button,
  Form,
  Input,
  Layout,
  LetterIcon,
  PhoneInput,
  SectionHeader,
  Text,
} from "@/shared/ui";
import { handleAxiosFieldErrors } from "@/shared/utils";

export const ContactDetails = () => {
  const { analytics } = useCustomerIoContext();
  const bmiInputs = useOnboardingStore((state) => state.bmiInputs);
  const personalDetails = useOnboardingStore((state) => state.personalDetails);
  const eligibilityAnswerIds = useOnboardingStore(
    (state) => state.eligibilityAnswerIds,
  );
  const contactDetails = useOnboardingStore((state) => state.contactDetails);
  const higherValue = useOnboardingStore(
    (state) => state.urlParams.higher_value,
  );
  const priceVariant = useOnboardingStore(
    (state) => state.urlParams.price_variant,
  );
  const couponCode = useOnboardingStore((state) => state.couponCode);

  const setContactDetails = useOnboardingStore(
    (state) => state.setContactDetails,
  );

  const queryClient = useQueryClient();

  useEffect(() => {
    trackEmailPhonePageViewed({ customerIoAnalytics: analytics });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const form = useForm({
    resolver: zodResolver(
      contactInfoSchema
        .pick({
          email: true,
          phoneNumber: true,
          smsMarketingConsent: true,
          emailMarketingConsent: true,
        })
        .extend({
          termsAndConditionsConsent: z.literal(true, {
            error: () => t`You must agree to the terms and conditions`,
          }),
        }),
    ),
    mode: "onTouched",
    defaultValues: {
      email: contactDetails?.email,
      phoneNumber: contactDetails?.phoneNumber,
      smsMarketingConsent: contactDetails?.smsMarketingConsent,
      emailMarketingConsent: contactDetails?.emailMarketingConsent ?? true,
    },
  });

  const createUser = useCreateUser();

  const navigate = useNavigate();

  if (!personalDetails) {
    return <Navigate to={ONBOARDING_ROUTE.ACCOUNT_PERSONAL_DETAILS} replace />;
  }

  const handleBack = () => {
    navigate(ONBOARDING_ROUTE.ACCOUNT_PERSONAL_DETAILS);
  };

  const handleNext = form.handleSubmit(
    ({ termsAndConditionsConsent: _termsAndConditionsConsent, ...values }) => {
      const userData = {
        ...bmiInputs,
        ...personalDetails,
        ...values,
        eligibilityAnswerIds: eligibilityAnswerIds ?? [],
        funnel: SIGNUP_FUNNEL.ORIGINAL_ONBOARDING,
        priceVariant,
      };

      // Prefetches choose-products.tsx's products query in parallel with the
      // user creation request, so it's already cached by the time we
      // navigate there and the page never shows its own loading spinner.
      // Awaited before navigating (below) so a slow prefetch can't lose the
      // race.
      const productsPrefetch = queryClient
        .prefetchQuery(
          productsQueriesStore.products.category._ctx.products({
            categorySlug: CATEGORY_SLUG.WEIGHT_LOSS,
            higherValue,
            priceVariant,
            couponCode,
          }),
        )
        .catch(() => undefined);

      createUser.mutate(userData, {
        onSuccess: (data) => {
          setContactDetails({
            billing: {
              address: "",
              apartmentNumber: "",
              city: "",
              zipCode: "",
            },
            shipping: {
              address: "",
              apartmentNumber: "",
              city: "",
              zipCode: "",
            },
            state: "",
            email: data.email,
            code: data.code,
            ehrId: data.ehrId,
            phoneNumber: userData.phoneNumber,
            smsMarketingConsent: userData.smsMarketingConsent ?? false,
            emailMarketingConsent: userData.emailMarketingConsent ?? true,
          });

          const everflowAttribution = {
            efTransactionId: data.efTransactionId,
            sourceId: data.sourceId,
            uid: data.uid,
            sub1: data.sub1,
            sub2: data.sub2,
            sub3: data.sub3,
            sub4: data.sub4,
            sub5: data.sub5,
          };

          if (data.affiliateId || hasEverflowAttribution(everflowAttribution)) {
            trackAffiliateIdCaptured({
              customerIoAnalytics: analytics,
              email: data.email,
              affiliateId: data.affiliateId,
              everflowAttribution,
            });
          }

          void productsPrefetch.then(() =>
            navigate(ONBOARDING_ROUTE.CHOOSE_TREATMENT),
          );
        },
        onError: (error) => handleAxiosFieldErrors(error, form.setError),
      });
    },
  );

  const termsAndConditionsLink = (
    <Link
      key="terms-and-conditions-link"
      to={EXTERNAL_LINK.TERMS_AND_CONDITIONS}
      className="underline"
      target="_blank"
    >
      {t`Terms & Conditions`}
    </Link>
  );

  return (
    <>
      <LogoLoader show={createUser.isPending} fullscreen />

      <Layout.Content>
        <SectionHeader
          title={
            <>
              {t`Enter your details to`}{" "}
              <span className="text-secondary-500">{t`activate!`}</span>
            </>
          }
          description={t`We respect your privacy - your details are used only for essential physician communication`}
        />

        <Form.Provider {...form}>
          <form
            onSubmit={handleNext}
            noValidate
            className="flex w-full flex-col gap-3 sm:max-w-xl sm:gap-7"
          >
            <Form.Field
              control={form.control}
              name="email"
              render={() => (
                <Form.Item className="mf-masked mf-listen">
                  <Form.Label aria-required>{t`Email`}</Form.Label>
                  <Form.Control>
                    <Input.Root
                      {...form.register("email")}
                      type="email"
                      placeholder={t`Enter your email`}
                    >
                      <Input.Slot>
                        <LetterIcon />
                      </Input.Slot>
                    </Input.Root>
                  </Form.Control>
                  <Form.Message compact={false} />

                  <Text size="small" color="light-neutral">
                    {jt`By entering your email, you agree to our ${termsAndConditionsLink} and consent to receive updates about your care. You can unsubscribe at any time.`}
                  </Text>
                </Form.Item>
              )}
            />

            <Form.Field
              control={form.control}
              name="phoneNumber"
              render={() => (
                <Form.Item className="mf-masked mf-listen">
                  <Form.Label aria-required>{t`Phone number`}</Form.Label>
                  <Form.Control>
                    <PhoneInput {...form.register("phoneNumber")} />
                  </Form.Control>
                  <Form.Message compact={false} />

                  <div className="-mt-2 flex flex-col gap-3 sm:items-center sm:text-center">
                    <Text weight="bold">
                      {t`We'll only use your number to text order updates and important information about your treatments.`}
                    </Text>

                    <SmsMarketingConsentField
                      control={form.control}
                      className="self-stretch text-left"
                    />
                  </div>
                </Form.Item>
              )}
            />

            <TermsAndConditionsField
              control={form.control}
              labelClassName="translate-y-1 text-xs"
            />

            <div className="col-span-full flex flex-col gap-3 pt-3 sm:pt-0">
              <Button size="lg" type="submit">
                {t`Continue`}
              </Button>

              <Button variant="secondary" size="lg" onClick={handleBack}>
                {t`Back`}
              </Button>
            </div>
          </form>
        </Form.Provider>

        <ProtectedInformation />
      </Layout.Content>
    </>
  );
};
